import { EventInteraction, useTracking } from '@eventbrite/ads';
import { $FixMe } from '@eventbrite/ts-utils';
import loadable from '@loadable/component';
import classNames from 'classnames';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { connect } from 'react-redux';
import { WaypointLazyRender } from '../../../../components/WaypointLazyRender';
import {
    ARTICLE_TEMPLATE,
    CARD_BIG,
    CARD_TEMPLATE_4,
    CARD_TEMPLATE_8,
    CATEGORY_TEMPLATE,
    COLLECTION_TEMPLATE,
    ENTITY_TYPE_MAP,
    EVENT_TIME_OPTIONS_TEMPLATE,
    FEATURED_TEMPLATE,
    LARGE_CARD_TEMPLATE_3,
    LARGE_CARD_TEMPLATE_6,
    NEARBY_CITIES_TEMPLATE,
    ORGANIZER_FOLLOW_TEMPLATE,
    TRENDING_SEARCHES_TEMPLATE,
} from '../../../../constants';
import {
    CITY_BROWSE_EVENT_VIEW_AFFILIATE,
    NEIGHBHOURHOOD_AFFILIATE,
    NIGHTLIFE_AFFILIATE,
    ONLINE_BROWSE_EVENT_VIEW_AFFILIATE,
} from '../../../../constants/analytics';
import { trackEventClickInternal as trackEventClickInternalAction } from '../../../../redux/actions/analytics';
import {
    BucketEntityType,
    BucketsContent,
    DiscoverState,
    FeatureFlags,
} from '../../../../types';
import { formatBucketLabelFromKey } from '../../../../utils/buckets';
import { useBrowseEventExpansions } from './expansionUtils';
import { aggregateContext } from './tracking';

const CollectionsBucket = loadable(
    () => import('../../../../components/FeaturedCollections'),
    { resolveComponent: (components: any) => components.CollectionsBucket },
);

const CardBucket = loadable(() => import('../../../../components/CardBucket'), {
    resolveComponent: (components: { CardBucket: React.ReactElement }) =>
        components.CardBucket,
});

const OrganizerFollowBucket = loadable(
    () => import('../OrganizerFollowBucket'),
    {
        resolveComponent: (components: {
            OrganizerFollowBucket: React.ReactElement;
        }) => components.OrganizerFollowBucket,
    },
);
const NearbyCitiesBucket = loadable(
    () => import('../../components/NearbyCitiesBucket'),
    {
        resolveComponent: (components: {
            NearbyCitiesBucket: React.ReactElement;
        }) => components.NearbyCitiesBucket,
    },
);
const TrendingSearchesBucket = loadable(
    () => import('../../components/TrendingSearchesBucket'),
    {
        resolveComponent: (components: {
            TrendingSearchesBucket: React.ReactElement;
        }) => components.TrendingSearchesBucket,
    },
);
const CategoryBucket = loadable(
    () => import('../../components/CategoryBucket'),
    {
        resolveComponent: (components: {
            CategoryBucket: React.ReactElement;
        }) => components.CategoryBucket,
    },
);
const EventTimeOptionsBucket = loadable(
    () => import('../../components/EventTimeOptionsBucket'),
    {
        resolveComponent: (components: {
            EventTimeOptionsBucket: React.ReactElement;
        }) => components.EventTimeOptionsBucket,
    },
);

export const BUCKET_LOOKUP_MAP: { [key: string]: React.ReactElement } = {
    [FEATURED_TEMPLATE]: CardBucket,
    [LARGE_CARD_TEMPLATE_3]: CardBucket,
    [LARGE_CARD_TEMPLATE_6]: CardBucket,
    [CARD_TEMPLATE_4]: CardBucket,
    [CARD_TEMPLATE_8]: CardBucket,
    [CARD_BIG]: CardBucket,
    [CATEGORY_TEMPLATE]: CategoryBucket,
    [NEARBY_CITIES_TEMPLATE]: NearbyCitiesBucket,
    [ARTICLE_TEMPLATE]: CardBucket,
    [TRENDING_SEARCHES_TEMPLATE]: TrendingSearchesBucket,
    [COLLECTION_TEMPLATE]: CollectionsBucket,
    [ORGANIZER_FOLLOW_TEMPLATE]: OrganizerFollowBucket,
    [EVENT_TIME_OPTIONS_TEMPLATE]: EventTimeOptionsBucket,
};

const DecorativeImage = ({
    decorationPlacement,
    decorativeImage,
}: Pick<BrowseBucketProps, 'decorationPlacement' | 'decorativeImage'>) => {
    let component = null;

    if (decorativeImage) {
        const classes = classNames(
            'decorative-image',
            `decorative-image--${decorationPlacement?.position}`,
            decorationPlacement?.classes,
        );
        const parentClasses = classNames(
            'decorative-image--container',
            decorationPlacement?.parentClasses,
        );

        component = (
            <div className={parentClasses}>
                <img className={classes} src={decorativeImage} alt="" />
            </div>
        );
    }

    return component;
};

const shouldReturnEmpty = (content: any, type: string, template: string) => {
    /*Future proofing such that if api adds additional buckets app doesn't break*/
    /*
    If content is undefined, that means the bucket type map has the appropriate key,
    but the value associated to it necessary to access the actual content in the response
    has changed. Solution is we should send an error, but not let the app fall over.
    */
    if (
        (!content || isEmpty(content)) &&
        template !== EVENT_TIME_OPTIONS_TEMPLATE
    ) {
        return true;
    }

    /* If the passed in type is not known in the ENTITY_TYPE_MAP this means that the API has
    started to return a layout format that is not known. The API should be allowed to progress
    without causing errors so no error should be fired. */
    if (!has(ENTITY_TYPE_MAP, type)) {
        return true;
    }

    /* If the passed in template is not known in the BUCKET_LOOKUP_MAP this means that the API has
    started to return a layout format that is not known. The API should be allowed to progress
    without causing errors so no error should be fired. */
    if (!has(BUCKET_LOOKUP_MAP, template)) {
        return true;
    }

    return false;
};

const EmptyBucket = (props: { type: string }) => {
    return <div key={props.type} className="empty-bucket" />;
};

interface BrowseBucketProps {
    onEnter?: Function;
    bucketIndex?: number;
    bucketKey: string;
    keyMin?: string;
    bucketWrapperClasses?: string;
    currentPlace?: string;
    containerClasses?: string;
    content?: BucketsContent;
    decorationPlacement?: {
        position: string[] | string;
        classes: string[] | string;
        parentClasses: string[] | string;
    };
    decorativeImage?: string;
    httpReferer?: string;
    linkUrl?: string;
    locationSlug?: string;
    name: string;
    onTrackEventClickInternal?: Function;
    template?: string;
    type: BucketEntityType;
    seeMoreMarkedNoFollow?: boolean;
    featureFlags?: FeatureFlags;
    isBotRequest?: boolean;
    trackEventClickInternal?: Function;
    seeMore?: {
        url?: string;
        params?: { dates?: string[]; places?: string[]; tags?: string[] };
    };
    inWindow?: boolean;
    isContentExpanded?: boolean;
    isOnline?: boolean;
    userId?: string;
    guestId?: string;
    placeId?: string;
    searchId?: string;
    sessionId?: string;
    locale: string;
    usesLanguageSubdirectory?: boolean;
    onRemoveEvent: (bucketKey: string, eventId: string) => void;
}

export const BrowseBucket = ({
    // onRemoveEvent is required by the BrowseBucketContainer but not all instances pass it as a prop.
    // fixing this is outside the scope of the current ticket but should be addressed.
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onRemoveEvent = () => {},
    ...props
}: BrowseBucketProps) => {
    const {
        content = [],
        seeMore,
        type,
        template = '',
        inWindow = false,
        isContentExpanded = false,
        placeId,
        isOnline,
        bucketIndex = 0,
        guestId,
        sessionId,
        userId,
        bucketKey,
        locale,
        searchId,
        currentPlace = '',
        name,
        containerClasses,
        bucketWrapperClasses,
        decorationPlacement,
        decorativeImage,
        trackEventClickInternal,
    } = props;
    const { url: linkUrl } = seeMore || { url: '' };

    const isEmptyBucket = shouldReturnEmpty(content, type, template);

    // Expand events in this bucket to get additional
    // data that we don't parse on the server
    useBrowseEventExpansions({
        content,
        placeId,
        isOnline,
        bucketKey,
        inWindow,
        isContentExpanded,
        type,
    });

    const getTrackingContext = ({ id, event }: EventInteraction) =>
        aggregateContext({
            id,
            event,
            events: content,
            location: {
                isOnline: isOnline,
                placeId: placeId,
            },
            user: {
                id: userId,
                guestId,
                sessionId,
            },
            bucketType: bucketKey,
            bucketIndex,
            locale,
            searchId,
        });
    const { ref } = useTracking<HTMLDivElement>(getTrackingContext);

    if (isEmptyBucket && template !== 'organizer_follow') {
        return <EmptyBucket type={type} />;
    }

    let affCode = isOnline
        ? ONLINE_BROWSE_EVENT_VIEW_AFFILIATE
        : CITY_BROWSE_EVENT_VIEW_AFFILIATE;
    if (bucketKey) {
        if (bucketKey == 'neighbourhood_events')
            affCode = NEIGHBHOURHOOD_AFFILIATE;
        else if (bucketKey == 'nightlife_events') affCode = NIGHTLIFE_AFFILIATE;
    }

    const DynamicBucket = BUCKET_LOOKUP_MAP[template] as any;
    const bucketLabel = formatBucketLabelFromKey({
        bucketKey,
        tabKey: '',
        name,
        currentPlace,
    });

    const promotedActionsData = {
        getTrackingContext,
        onRemoveEvent: (eventId: string) => onRemoveEvent(bucketKey, eventId),
    };

    return (
        <div className={containerClasses} key={bucketKey} ref={ref}>
            <div
                className={bucketWrapperClasses}
                data-event-bucket-label={bucketLabel}
                data-event-bucket-key={bucketKey}
            >
                <DecorativeImage
                    decorationPlacement={decorationPlacement}
                    decorativeImage={decorativeImage}
                />
                {
                    // This is a legacy component that combines all possible buckets into a single one
                    // This is a problem because it makes it hard to reason about the component and its behavior
                    // Props are not strongly typed, resulting in a enourmous component that is hard to maintain
                    // TODO: Re-think data format and component structure
                }
                <DynamicBucket
                    linkUrl={linkUrl}
                    onTrackEventClickInternal={trackEventClickInternal}
                    affCode={affCode}
                    statsigLocationString={`CityBrowse - ${bucketKey}`}
                    promotedActionsData={promotedActionsData}
                    {...props}
                />
            </div>
        </div>
    );
};

const LazyRenderBrowseBucket: React.FunctionComponent<BrowseBucketProps> = (
    props,
) => {
    const { bucketIndex, onEnter, bucketKey } = props;
    const [inWindow, setIsInWindow] = React.useState(false);

    const handleEnter = () => {
        setIsInWindow(true);
        onEnter?.();
    };

    return (
        <WaypointLazyRender
            initialIsVisible={
                props.isBotRequest ||
                (typeof bucketIndex === 'number' && bucketIndex <= 3) ||
                bucketKey === 'neighbourhood_events'
            }
            emptyStateStyles={{ height: '300px' }}
            onEnter={handleEnter}
        >
            <BrowseBucket {...props} inWindow={inWindow} />
        </WaypointLazyRender>
    );
};

export const _mapStateToProps = (
    state: DiscoverState,
    ownProps: BrowseBucketProps,
) => {
    const {
        containerClasses,
        bucketWrapperClasses,
        decorationPlacement,
        decorativeImage,
    } = ownProps;

    return {
        containerClasses,
        bucketWrapperClasses,
        decorationPlacement,
        decorativeImage,
        currentPlace: state.location.currentPlace,
        httpReferer: state.app.httpReferer,
        locationSlug: state.location?.slug,
        locale: state.app.locale,
        usesLanguageSubdirectory: state.app.uses_language_subdirectory,
        featureFlags: state.app.featureFlags,

        userId: state.app.publicId,
        guestId: state.app.guestId,
        placeId: state.location.placeId,
        isOnline: state.location.isOnline,
        isBotRequest: state.app.isBotRequest,
        searchId: state.search.searchId,
        sessionId: state.app.sessionId,
        options: state.app.options,
    };
};
const _mapDispatchToProps = (dispatch: Function) => ({
    trackEventClickInternal: (...args: [$FixMe]) =>
        dispatch(trackEventClickInternalAction(...args)),
});

export const BrowseBucketContainer = connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(LazyRenderBrowseBucket);

export default BrowseBucketContainer;
